<script setup>
import { ref, watch } from 'vue'
import Dialog from 'primevue/dialog'
import AutoComplete from 'primevue/autocomplete'
import Button from 'primevue/button'
import Checkbox from 'primevue/checkbox'
import { useToast } from 'primevue/usetoast'
import { useResponsiblesStore } from '@/stores/responsibles.js'
import { useEmployeeStore } from '@/stores/employee.js'

const props = defineProps({
  visible: { type: Boolean, default: false },
  entityType: { type: String, required: true },
  entityId: { type: [Number, String], required: true },
  existingCount: { type: Number, default: 0 },
})
const emit = defineEmits(['update:visible', 'saved', 'close'])

const toast = useToast()
const responsiblesStore = useResponsiblesStore()
const employeesStore = useEmployeeStore() // ⬅️ НОВОЕ

const selectedEmployee = ref(null) // { id, full_name }
const suggestions = ref([])
const makePrimary = ref(false)
const loading = ref(false)
let searchTimer = null

watch(() => props.visible, async (v) => {
  if (v) {
    // По умолчанию «сделать основным», если никого нет
    makePrimary.value = (props.existingCount || 0) === 0
    await preloadEmployees() // ⬅️ через стор
  } else {
    selectedEmployee.value = null
    suggestions.value = []
    loading.value = false
  }
})

function close() {
  emit('update:visible', false)
  emit('close')
}

async function preloadEmployees() {
  loading.value = true
  try {
    await employeesStore.loadEmployees({ page: 1, is_active: true })
    const items = employeesStore.employees || []
    suggestions.value = items.map(it => ({
           id: it.id,
           full_name: it.full_name,
           department: it.department,
           post: it.post,
         }))
  } catch (err) {
    console.error('Ошибка предзагрузки сотрудников', err)
    suggestions.value = []
  } finally {
    loading.value = false
  }
}

async function completeEmployees(e) {
  const q = String(e?.query ?? '').trim()
  if (searchTimer) clearTimeout(searchTimer)
  searchTimer = setTimeout(async () => {
    loading.value = true
    try {
      await employeesStore.loadEmployees({ page: 1, search: q, is_active: true })
      const items = employeesStore.employees || []
      suggestions.value = items.map(it => ({
            id: it.id,
            full_name: it.full_name,
            department: it.department,
            post: it.post,
          }))
    } catch (err) {
      console.error('Ошибка загрузки сотрудников', err)
      suggestions.value = []
    } finally {
      loading.value = false
    }
  }, 300)
}

async function submit() {
  if (!selectedEmployee.value?.id) {
    toast.add({ severity: 'warn', summary: 'Заполните форму', detail: 'Выберите сотрудника', life: 2500 })
    return
  }
  const nextPriority = Math.min((props.existingCount || 0) + 1, 3)
  if (nextPriority > 3) {
    toast.add({ severity: 'warn', summary: 'Нельзя добавить', detail: 'Доступно максимум 3 ответственных', life: 3000 })
    return
  }

  try {
    await responsiblesStore.assign(props.entityType, Number(props.entityId), {
      user_id: selectedEmployee.value.id,
      priority: nextPriority,
      make_primary: !!makePrimary.value,
    })
    toast.add({ severity: 'success', summary: 'Ответственный добавлен', life: 2000 })
    emit('saved')
    close()
  } catch (e) {
    const status = e?.response?.status
    const detail = e?.response?.data?.detail
    const msg = status === 409 ? 'Конфликт слотов: попробуйте обновить страницу' : (detail || 'Не удалось назначить')
    toast.add({ severity: 'error', summary: 'Ошибка', detail: msg, life: 4000 })
  }
}
</script>
<template>
  <Dialog
      :visible="visible"
      @update:visible="(v) => v ? null : close()"
      modal
      header="Добавить ответственного"
      :style="{ width: '520px', maxWidth: '95vw' }"
      :closable="true"
      :position="'top'"
  >
    <form class="flex flex-col gap-4" @submit.prevent="submit">
      <AutoComplete
          inputId="employee"
          v-model="selectedEmployee"
          optionLabel="full_name"
          :suggestions="suggestions"
          :loading="loading"
          @complete="completeEmployees"
          placeholder="Выберите сотрудника"
          :completeOnFocus="true"

      >
        <template #option="{ option }">
          <div class="flex items-center gap-2">
            <div class="flex flex-col">
              <span>{{ option.full_name }}</span>
              <small class="text-gray-500">
                {{ option.department }}<span v-if="option.post"> • {{ option.post }}</span>
              </small>
            </div>
          </div>
        </template>

      </AutoComplete>


      <div class="flex items-center gap-2 mt-1">
        <Checkbox inputId="make_primary" v-model="makePrimary" binary />
        <label for="make_primary" class="text-sm cursor-pointer select-none">
          Сделать основным
        </label>
      </div>

      <div class="flex justify-center gap-3 pt-2">
        <Button label="Добавить" type="submit" severity="info" rounded />
        <Button label="Отмена" type="button" severity="secondary" rounded @click="close" />
      </div>
    </form>
  </Dialog>
</template>

<style scoped>
:deep(.p-autocomplete) { display: flex; width: 100%; }
:deep(.p-autocomplete .p-autocomplete-input) { width: 100%; }
</style>